import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { filter, forkJoin } from 'rxjs';
import { DriverAttributeType } from '../../../driver/models/enums/driver-attribute-type.enum';
import { DriverMedicalCertificateStatusType } from '../../../driver/models/enums/driver-medical-certificate-status-type.enum';
import { MedicalExamCertificateDecisionType } from '../../../driver/models/enums/medical-exam-certificate-decision-type.enum';
import { MedicalExamCertificateRestrictionType } from '../../../driver/models/enums/medical-exam-certificate-restriction-type.enum';
import { MedicalExamCertificateRestriction } from '../../../driver/models/requirement/medical-exam-certificate-restriction.model';
import { MedicalExamCertificate } from '../../../driver/models/requirement/medical-exam-certificate.model';
import { AccessControlType } from '../../../foundation/models/enums/access-control-type.enum';
import { Jurisdiction } from '../../../foundation/models/enums/Jurisdiction.enum';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { ModalSize } from '../../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { MedicalCertificateRestrictionTypeDriverAttributeTypeMap } from '../../models/medical-exam-certificate-restriction-type-driver-attribute-type-map.model';
import { MedicalReviewReportContext } from '../../models/medical-review-report-context.model';
import { MedicalReviewService } from '../../services/medical-review.service';
import { MedicalExamCertificateModalComponent } from '../medical-exam-certificate/medical-exam-certificate-modal.component';
import { CustomerMrpCertificateStatusComponent } from './customer-mrp-certificate-status.component';
import { HistoricalCertificatesContext, HistoricalCustomerMrpCertificatesModal } from './historical-customer-mrp-certificates-modal.component';

@Component({
    selector: 'customer-mrp-certificates',
    templateUrl: './customer-mrp-certificates.html',
})
export class CustomerMrpCertificatesComponent extends BaseComponent {
    @Input() public customerNumberId: number;
    @Input() public editorEnabled: boolean;
    @Output() public certEdit = new EventEmitter<void>();
    public canDeleteMedCert = false;
    public certs: MedicalExamCertificate[] = [];
    public topOfStackCert: MedicalExamCertificate;
    private jurisdictionTypeEnum = new Jurisdiction();
    private restrictionTypeEnum: MedicalExamCertificateRestrictionType = new MedicalExamCertificateRestrictionType();
    private driverMedicalCertificateStatusTypeEnum = new DriverMedicalCertificateStatusType();
    private medicalExamCertificateDecisionTypeEnum = new MedicalExamCertificateDecisionType();
    private driverAttributeTypeEnum = new DriverAttributeType();
    private accessControlType: AccessControlType = new AccessControlType();
    private medicalCertificateRestrictionTypeDriverAttributeTypeMaps: MedicalCertificateRestrictionTypeDriverAttributeTypeMap[] = [];

    constructor(
        private medicalReviewService: MedicalReviewService,
        private modalService: MvdModalService
    ) {
        super();
    }

    public ngOnInit() {
        this.canDeleteMedCert = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, this.accessControlType.AllowRemoveMedicalCertificate.value);
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (null != changes.customerNumberId) {
            this.loadCerts(changes.customerNumberId.currentValue);
        }
    }

    public refreshCertificates(): void {
        if (null != this.customerNumberId) {
            this.loadCerts(this.customerNumberId);
        }
    }

    public showHistory = (): void => {
        let data = new HistoricalCertificatesContext(this.customerNumberId, this.topOfStackCert.id);

        this.modalService.open(HistoricalCustomerMrpCertificatesModal, data);
    };

    public createMedicalExamCertificateModel = (): void => {
        let data = new MedicalReviewReportContext(this.customerNumberId, null, null);
        data.createNewReport = true;
        data.isReadOnly = false;
        this.modalService.open(MedicalExamCertificateModalComponent, data)
            .afterClosed()
            .subscribe(
                () => {
                    this.certEdit.emit();
                    this.loadCerts(this.customerNumberId);
                });
    };

    public openMedicalExamCertificateModal = (certificate: MedicalExamCertificate, readOnly: boolean): void => {
        let data = new MedicalReviewReportContext(this.customerNumberId, null, null);
        data.isReadOnly = readOnly;
        this.modalService.open(MedicalExamCertificateModalComponent, data)
            .afterClosed()
            .subscribe(
                () => {
                    if (readOnly == false) {
                        this.certEdit.emit();
                        this.loadCerts(this.customerNumberId);
                    }
                });
    };

    public openMedicalExamCertificateStatusModal = (certificate: MedicalExamCertificate): void => {
        this.modalService.open(CustomerMrpCertificateStatusComponent, certificate, ModalSize.Small)
            .afterClosed()
            .subscribe(
                () => {
                    this.certEdit.emit();
                    this.loadCerts(this.customerNumberId);
                });
    };

    public openDeleteMedicalExamCertificateModal(certificate: MedicalExamCertificate) {
        const message = 'Are you sure you want to delete your Medical Exam Certificate? This action cannot be reversed.';
        const title = 'Confirm Delete';
        this.modalService.confirm().title(title).body(message)
            .okBtn('Confirm').cancelBtn('Cancel')
            .open(ModalSize.Small).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(
                () => {
                    this.medicalReviewService.removeMedicalExamCertificate(certificate).subscribe(() => {
                        this.certEdit.emit();
                        this.loadCerts(this.customerNumberId);
                    });
                });
    }

    private loadCerts = (customerNumberId: number): void => {
        this.certs = [];
        forkJoin(this.medicalReviewService.getTopOfStackMedicalExamCertificate(customerNumberId), this.medicalReviewService.getDriverMedicalCertificateRestrictionTypeDriverAttributeTypeMap())
            .subscribe(([cert, maps]) => this.prepareCerts(cert, maps));
    };

    private prepareCerts = (certificate: MedicalExamCertificate, maps: any): void => {
        this.medicalCertificateRestrictionTypeDriverAttributeTypeMaps = maps.result;

        if (null != certificate.customerNumberId &&
            certificate.driverMedicalCertificateStatusTypeId != this.driverMedicalCertificateStatusTypeEnum.Removed.id) {
            this.topOfStackCert = certificate;
            this.certs.push(certificate);

            this.certs?.forEach(item => {
                item.jurisdictionDisplayValue = this.jurisdictionTypeEnum.FromId(item.medicalExamCertificateIssuingJurisdictionId)?.displayValue ?? '';
                item.decisionDisplayValue = this.medicalExamCertificateDecisionTypeEnum.FromId(item.medicalExamCertificateDecisionTypeId)?.displayValue ?? '';
                item.driverMedicalCertificateStatusTypeDisplayValue = this.driverMedicalCertificateStatusTypeEnum.FromId(item.driverMedicalCertificateStatusTypeId)?.displayValue ?? '';
                this.loadRestrictionDisplayValues(item, item.restrictions);
            });
        }
    };

    private loadRestrictionDisplayValues = (cert: MedicalExamCertificate, restrictions: MedicalExamCertificateRestriction[]): void => {
        const restrictionValues: string[] = [];
        const restrictionCodeValues: string[] = [];

        restrictions?.forEach(e => {
            const restrictionDisplayValue = this.restrictionTypeEnum.FromId(e.restrictionTypeId)?.displayValue;

            if (restrictionDisplayValue != null) {
                restrictionValues.push(restrictionDisplayValue);
            }

            const attributeTypeId = this.medicalCertificateRestrictionTypeDriverAttributeTypeMaps
                .find(m => m.driverMedicalCertificateRestrictionTypeId === e.restrictionTypeId)?.driverAttributeTypeId;

            if (attributeTypeId != null) {
                const attribute = this.driverAttributeTypeEnum.FromId(attributeTypeId);
                restrictionCodeValues.push(attribute.driverAttributeValue);
            }
        });

        cert.restrictionCodesDisplayValues = restrictionCodeValues.join(',');
        cert.restrictionDisplayValues = restrictionValues.join(',');
    };
}
